import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
  }).format(amount)
}

export function formatDate(date: Date | string): string {
  // For date-only strings ("2026-02-05"), append T00:00:00 to avoid
  // UTC interpretation which shifts the date back in negative UTC offsets
  const d = typeof date === 'string'
    ? new Date(date.length === 10 ? `${date}T00:00:00` : date)
    : date
  return new Intl.DateTimeFormat('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(d)
}

/** formatDate null-safe: '-' para null/undefined/'' o fecha inválida (tablas y diálogos). */
export function formatDateOrDash(date: string | null | undefined): string {
  if (!date) return '-'
  try {
    return formatDate(date)
  } catch {
    return '-'
  }
}

/** Fecha larga "15 de junio de 2026" (contratos y documentos). Sin arg = hoy. */
export function formatDateLong(date?: Date | string): string {
  const d = !date
    ? new Date()
    : typeof date === 'string'
      ? new Date(date.length === 10 ? `${date}T00:00:00` : date)
      : date
  return new Intl.DateTimeFormat('es-AR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(d)
}

/** Fecha + hora larga "15 de junio de 2026, 14:05:33" (detalle de email/logs). */
export function formatDateTime(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date
  return d.toLocaleString('es-AR', {
    dateStyle: 'long',
    timeStyle: 'medium',
  })
}

/**
 * Genera un enlace de WhatsApp a partir de un número de teléfono
 * @param telefono Número de teléfono (puede incluir espacios, guiones, etc.)
 * @param mensaje Mensaje predeterminado opcional
 * @returns URL de WhatsApp o null si no hay teléfono
 */
export function generarEnlaceWhatsApp(telefono: string | null | undefined, mensaje?: string): string | null {
  if (!telefono) return null

  // Limpiar el número: quitar espacios, guiones, paréntesis
  let numeroLimpio = telefono.replace(/[\s\-().]/g, '')

  // Si empieza con 0, asumir que es número argentino y convertir
  if (numeroLimpio.startsWith('0')) {
    numeroLimpio = '54' + numeroLimpio.slice(1)
  }

  // Si no tiene código de país, agregar Argentina (54)
  if (!numeroLimpio.startsWith('+') && !numeroLimpio.startsWith('54')) {
    numeroLimpio = '54' + numeroLimpio
  }

  // Quitar el + si existe
  numeroLimpio = numeroLimpio.replace('+', '')

  // Construir URL
  const baseUrl = `https://wa.me/${numeroLimpio}`

  if (mensaje) {
    return `${baseUrl}?text=${encodeURIComponent(mensaje)}`
  }

  return baseUrl
}

/**
 * Valida un CUIT argentino
 * El CUIT tiene el formato XX-XXXXXXXX-X donde:
 * - Los primeros 2 dígitos son el tipo (20, 23, 24, 27, 30, 33, 34)
 * - Los siguientes 8 dígitos son el número de documento o identificador
 * - El último dígito es el verificador
 *
 * @param cuit CUIT a validar (puede incluir guiones o no)
 * @returns Objeto con el resultado de la validación
 */
export function validarCUIT(cuit: string): { valido: boolean; error?: string; cuitFormateado?: string } {
  if (!cuit) {
    return { valido: false, error: 'El CUIT es requerido' }
  }

  // Limpiar el CUIT: quitar guiones, espacios y puntos
  const cuitLimpio = cuit.replace(/[-.\s]/g, '')

  // Verificar que tenga 11 dígitos
  if (cuitLimpio.length !== 11) {
    return { valido: false, error: 'El CUIT debe tener 11 dígitos' }
  }

  // Verificar que solo contenga números
  if (!/^\d+$/.test(cuitLimpio)) {
    return { valido: false, error: 'El CUIT solo debe contener números' }
  }

  // Verificar el tipo (primeros 2 dígitos)
  const tipo = cuitLimpio.substring(0, 2)
  const tiposValidos = ['20', '23', '24', '27', '30', '33', '34']
  if (!tiposValidos.includes(tipo)) {
    return { valido: false, error: 'El tipo de CUIT no es válido' }
  }

  // Calcular el dígito verificador
  const multiplicadores = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2]
  let suma = 0

  for (let i = 0; i < 10; i++) {
    suma += parseInt(cuitLimpio[i]) * multiplicadores[i]
  }

  const resto = suma % 11
  let digitoVerificadorCalculado: number

  if (resto === 0) {
    digitoVerificadorCalculado = 0
  } else if (resto === 1) {
    // En este caso el CUIT es inválido según algunas implementaciones,
    // pero otras asignan 9 si el tipo es 23 (mujer) o 0 en otros casos
    if (tipo === '23') {
      digitoVerificadorCalculado = 9
    } else {
      digitoVerificadorCalculado = 0
    }
  } else {
    digitoVerificadorCalculado = 11 - resto
  }

  const digitoVerificadorReal = parseInt(cuitLimpio[10])

  if (digitoVerificadorCalculado !== digitoVerificadorReal) {
    return { valido: false, error: 'El dígito verificador es incorrecto' }
  }

  // Formatear el CUIT
  const cuitFormateado = `${cuitLimpio.substring(0, 2)}-${cuitLimpio.substring(2, 10)}-${cuitLimpio.substring(10)}`

  return { valido: true, cuitFormateado }
}

/**
 * Formatea un CUIT agregando los guiones
 * @param cuit CUIT sin formato
 * @returns CUIT formateado (XX-XXXXXXXX-X)
 */
export function formatearCUIT(cuit: string): string {
  const cuitLimpio = cuit.replace(/[-.\s]/g, '')
  if (cuitLimpio.length !== 11) return cuit
  return `${cuitLimpio.substring(0, 2)}-${cuitLimpio.substring(2, 10)}-${cuitLimpio.substring(10)}`
}

/**
 * Obtiene el período actual en formato "Mes AAAA"
 * @returns Período actual (ej: "Diciembre 2025")
 */
export function obtenerPeriodoActual(): string {
  const ahora = new Date()
  const mes = ahora.toLocaleString('es-AR', { month: 'long' })
  const anio = ahora.getFullYear()
  return `${mes.charAt(0).toUpperCase() + mes.slice(1)} ${anio}`
}

/**
 * Capitaliza correctamente nombres propios.
 * Convierte "ESTEBAN FERNANDO" → "Esteban Fernando"
 * Convierte "esteban fernando" → "Esteban Fernando"
 * Maneja casos especiales: "DE LA", "DEL", "VON", etc.
 * Nota: Múltiples espacios internos se colapsan a uno solo.
 *
 * @param texto Texto a capitalizar
 * @returns Texto capitalizado correctamente
 */
export function capitalizarNombre(texto: string): string {
  if (!texto) return texto

  // Palabras que deben ir en minúscula (excepto al inicio)
  const particulas = ['de', 'del', 'la', 'los', 'las', 'y', 'e', 'von', 'van', 'da', 'di']

  // Preservar espacios al inicio/fin durante la escritura
  const espaciosInicio = texto.match(/^\s*/)?.[0] || ''
  const espaciosFin = texto.match(/\s*$/)?.[0] || ''

  // Trabajar solo con el texto sin espacios extremos
  const textoLimpio = texto.trim()
  if (!textoLimpio) return texto // Si solo hay espacios, devolver tal cual

  // Procesar el texto
  const resultado = textoLimpio
    .toLowerCase()
    .split(/\s+/) // Split por uno o más espacios
    .map((palabra, index) => {
      if (!palabra) return palabra // Preservar strings vacíos
      // Si es una partícula y NO es la primera palabra, dejar en minúscula
      if (index > 0 && particulas.includes(palabra)) {
        return palabra
      }
      // Capitalizar primera letra
      return palabra.charAt(0).toUpperCase() + palabra.slice(1)
    })
    .join(' ')

  // Restaurar espacios originales al inicio/fin
  return espaciosInicio + resultado + espaciosFin
}

/**
 * Calcula el equivalente mensual de un precio según su frecuencia
 * @param precio Precio del servicio
 * @param frecuencia Frecuencia de facturación
 * @returns Equivalente mensual del precio
 */
export function calcularEquivalenteMensual(
  precio: number,
  frecuencia: 'mensual' | 'bimestral' | 'trimestral' | 'semestral' | 'anual' | 'unico'
): number {
  const divisores: Record<string, number> = {
    mensual: 1,
    bimestral: 2,
    trimestral: 3,
    semestral: 6,
    anual: 12,
    unico: 0, // No se incluye en total mensual
  }
  const divisor = divisores[frecuencia] || 1
  return divisor > 0 ? precio / divisor : 0
}

/**
 * Calcula el total mensual de una lista de servicios
 * @param servicios Lista de servicios con precio y frecuencia
 * @returns Total mensual
 */
export function calcularTotalMensual(
  servicios?: Array<{ precio: number; frecuencia: string }>
): number {
  if (!servicios?.length) return 0
  return servicios.reduce((sum, s) => {
    return sum + calcularEquivalenteMensual(
      s.precio,
      s.frecuencia as 'mensual' | 'bimestral' | 'trimestral' | 'semestral' | 'anual' | 'unico'
    )
  }, 0)
}
